import React, { useState, useEffect, useCallback } from "react";
import publicAxios from "../../utils/publicAxios";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import {
  showToast,
  isValidPhoneLength,
  isRecaptchaEnabled,
  normalizeLegalName
} from "../../utils/utils";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  FaEye,
  FaEyeSlash,
  FaCheck,
  FaInfoCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaArrowRight
} from "react-icons/fa";
import "./../../assets/scss/login.scss";
import AuthFooter from "../../components/layout/footer/AuthFooter";
import AuthMechanism from "../../components/auth/AuthMechanism";
import AuthHeader from "../../components/auth/AuthHeader";
import useMixpanelEventAction from "../../config/mixpanel/useMixpanelEventAction";
import GA4EventAction from "../../config/GoogleAnalytics/useGA4EventAction";
import {
  profiletexts,
  signup,
  login,
  passwordtext,
  Inputfieldvalidation,
  commonErrMsg
} from "../../locales/en";
import {
  ALLOWED_KEYS,
  AU_PHONE_REGEX,
  COUNTRY_CODE_AU_SMALL,
  COUNTRY_CODE_NZ,
  COUNTRY_CODE_NZ_SMALL,
  DIGIT_REGEX,
  EMAIL_REGEX_SIMPLE,
  ESCAPE_REGEX,
  INDIAN_CURRENCY,
  IP_API_URL,
  LEGAL_NAME_REGEX,
  LOWERCASE_REGEX,
  NZ_PHONE_REGEX,
  PH_LENGTH_11,
  PH_LENGTH_13,
  REMOVE_NON_DIGIT_REGEX,
  showToastType,
  SINGLE_DIGIT_REGEX,
  UPPERCASE_REGEX,
  WHITESPACE_REGEX,
  AREA_CODES
} from "../../utils/constants/appConstants";
import {
  RECAPTCHA_ERROR,
  EXPIRY_ERROR
} from "../../utils/constants/appConstants";
import { getFetchCountryCodeURL } from "../../utils/constants/apiEndPoints";
import { getRegisterUrl } from "../../utils/constants/apiEndPoints";
import { INVOICE, LOGIN } from "../../route/paths";
import {
  buildUtmHistory,
  buildHubspotFormContext
} from "../../utils/hubspotUtils";
import {
  buildMarketingIdentifiers,
  clearStoredAdClickIds
} from "../../utils/marketingIdentifiers";
import { NAVIGATION_SOURCE } from "../../utils/constants/navigationConstants";
import { persistUserRoles } from "../../utils/accountRoles";
import {
  setDisconnectConnectedSoftwareData,
  setGetConnectedSoftwareData,
  setOnboardingData
} from "../../redux/loginSlice";
import { useDispatch } from "react-redux";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import EmailVerificationStep from "./EmailVerification";
import useSignupEmailOtp from "../../hooks/signup/useSignupEmailOtp";

// Which form field a server-side field error belongs beside. The server names the
// fields of its own register payload, which is not quite what the form calls them.
const SERVER_FIELD_TO_FORM_FIELD = {
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  password: "password",
  primaryContactNumber: "phoneNumber"
};

/**
 * Sorts a validation failure into the fields it names and the messages it does not.
 *
 * The server sends field errors as one or more objects of field name to messages. A
 * field the form has an input for is answered beside that input; anything else has
 * nowhere to sit, so it is handed back for the caller to show on its own.
 *
 * @param {*} errors the errors from a "Validation failed" response
 * @returns {{ fieldErrors: Object, otherMessages: string[] }} what to show, and where
 */
const splitValidationErrors = (errors) => {
  const fieldErrors = {};
  const otherMessages = [];

  const entries = Array.isArray(errors) ? errors : [errors];
  entries.forEach((entry) => {
    if (entry && typeof entry === "object" && !Array.isArray(entry)) {
      Object.entries(entry).forEach(([field, messages]) => {
        const text = [].concat(messages).flat(Infinity).join("\n");
        const formField = SERVER_FIELD_TO_FORM_FIELD[field];
        if (formField) {
          fieldErrors[formField] = text;
        } else {
          otherMessages.push(text);
        }
      });
    } else if (entry !== null && entry !== undefined) {
      otherMessages.push(String(entry));
    }
  });

  return { fieldErrors, otherMessages };
};

const Signup = () => {
  const dispatch = useDispatch();
  const environment = process.env.REACT_APP_ENV;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { signupMixpanelEvent } = useMixpanelEventAction();
  const { signupGA4Event } = GA4EventAction();
  const [selectCountry, setSelectCountry] = useState(COUNTRY_CODE_AU_SMALL);
  const [country, setCountry] = useState(COUNTRY_CODE_AU_SMALL);
  const [validatePhnumber, setValidatePhnumber] = useState(false);
  const { promoCode, referralPromoUsageHistoryId } = useParams();
  const initialLoading = {
    signUpBtn: false
  };
  const initialValue = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    showPassword: false,
    error: ""
  };
  const initialErrors = {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    submit: ""
  };

  const [loading, setLoading] = useState(initialLoading);
  const [values, setValues] = useState(initialValue);
  const [errors, setErrors] = useState(initialErrors);
  const emailOtp = useSignupEmailOtp();

  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("referral_code");

  const handleClickShowPassword = () => {
    setValues((prevValues) => ({
      ...prevValues,
      showPassword: !prevValues.showPassword
    }));
  };

  const fetchUserLocation = useCallback(async () => {
    try {
      const response = await fetch(IP_API_URL);
      const data = await response.json();
      if (data.country_code === COUNTRY_CODE_NZ) {
        setCountry(COUNTRY_CODE_NZ_SMALL);
      } else {
        setCountry(COUNTRY_CODE_AU_SMALL);
      }
    } catch {
      //handle error
    }
  }, []);

  // Updated from the previous state rather than the render's `values`, so a
  // keystroke landing before the last one has re-rendered cannot spread a stale
  // snapshot back over the other fields.
  const handleInputChange = (field, value) => {
    setValues((prevValues) => ({ ...prevValues, [field]: value }));
  };

  const fetchCountryCode = useCallback(async (phoneNumber, country) => {
    try {
      const response = await publicAxios.get(getFetchCountryCodeURL, {
        params: { number: phoneNumber, countryCode: country }
      });
      if (response.status === 200) {
        setValidatePhnumber(true);
      } else {
        setValidatePhnumber(false);
      }
    } catch {
      setValidatePhnumber(false);
    }
  }, []);

  const selectedCountry = (phoneNumber) => {
    const countryCode = phoneNumber.slice(0, 2);
    let country = null;
    if (countryCode === "61") {
      country = "au";
    } else if (countryCode === "64") {
      country = "nz";
    }
    return country;
  };

  const requestPhoneNumber = (phoneNumnber) => {
    const result = phoneNumnber.slice(2).replace(/[()-\s]/g, "");
    return result;
  };

  const handlePhInputChange = (field, value, countryCode) => {
    setValues((prevValues) => ({ ...prevValues, [field]: value }));
    setValidatePhnumber(false);
    setErrors((prev) => ({ ...prev, phoneNumber: "" })); // clear a stale "required" as the user types

    if (selectCountry !== countryCode) {
      // silent: gate the country lookup without flashing a "too short" error
      // while the user is still typing — the phone error shows on blur instead.
      const result = validateField(field, value, true);
      if (result !== "") return;
      const phoneNumber = requestPhoneNumber(value);
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };

  const handleBlur = (field, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [field]: value
    }));
    // Trigger validation for the specific field
    const result = validateField(field, value);
    if (result !== "") return;
    const phoneNumber = requestPhoneNumber(values.phoneNumber);
    if (phoneNumber && phoneNumber !== value) {
      fetchCountryCode(phoneNumber, selectedCountry(value));
    }
  };
  // NFC-normalises a name, folds every run of whitespace (including the
  // non-breaking space that browser autofill injects) to one ASCII space, and
  // trims. Both the name validation and the submit payload use it so what is
  // checked is what is sent, and the backend receives a clean value.
  const cleanName = (value) =>
    normalizeLegalName(value ?? "")
      .replace(/\s+/g, " ")
      .trim();

  // silent=true computes and returns the error without writing it to state — used
  // for on-change gating (deciding whether to look up the phone's country) so we
  // don't punish the user with an error before they've finished typing. The error
  // is surfaced on blur, where validateField is called normally.
  const validateField = (field, value, silent = false) => {
    let error = "";
    switch (field) {
      case "firstName": {
        // Measure the cleaned value the backend receives, not the raw input: a
        // decomposed accented letter is two code units raw but one after NFC, so
        // a raw-length check would pass a name /register then rejects on @Size.
        const cleaned = cleanName(value);
        if (cleaned.length === 0) {
          error = profiletexts?.errorMessage.firstNameRequired;
        } else if (!LEGAL_NAME_REGEX.test(cleaned)) {
          error = signup?.errors.firstnameErr;
        } else if (cleaned.length < 2 || cleaned.length > 50) {
          error = Inputfieldvalidation.errorMsg.firstNameLength;
        }
        break;
      }

      case "lastName": {
        const cleaned = cleanName(value);
        if (cleaned.length === 0) {
          error = profiletexts?.errorMessage.lastNameRequired;
        } else if (!LEGAL_NAME_REGEX.test(cleaned)) {
          error = signup?.errors.lastnameErr;
        } else if (cleaned.length < 2 || cleaned.length > 50) {
          error = Inputfieldvalidation.errorMsg.lastNameLength;
        }
        break;
      }

      case "email":
        if (value.length > 100) {
          error = login?.errorMSG.emailLong;
        } else {
          error = EMAIL_REGEX_SIMPLE.test(value)
            ? ""
            : login?.errorMSG.enterEmail;
        }
        break;

      case "phoneNumber": {
        const trimmedValue = value.replace(WHITESPACE_REGEX, "");
        const numericValue = value.trim().replace(/[^\d]/g, "");

        if (
          AU_PHONE_REGEX.test(trimmedValue) ||
          NZ_PHONE_REGEX.test(trimmedValue)
        ) {
          error = signup?.errors.startnumber;
        } else if (
          trimmedValue === "61" ||
          trimmedValue === "64" ||
          trimmedValue === ""
        ) {
          error = signup?.errors?.numberRequired;
        } else if (!isValidPhoneLength(numericValue)) {
          error = profiletexts?.errorMessage.primaryContactError;
        }
        break;
      }

      case "password":
        {
          const firstName = values.firstName.trim().toLowerCase();
          const emailPrefix = values.email.split("@")[0].trim().toLowerCase();
          const passwordLower = value.toLowerCase();

          if (!value || value.trim().length === 0) {
            error = signup?.errors.passRequired;
          } else if (value.length < 8 || value.length > 50) {
            error = Inputfieldvalidation.errorMsg.passwordLength;
          } else if (
            (firstName && passwordLower.startsWith(firstName)) ||
            (emailPrefix && passwordLower.startsWith(emailPrefix))
          ) {
            error = Inputfieldvalidation.errorMsg.passwordStartWithPart;
          } else {
            const passwordErrors = [];
            if (value.includes(INDIAN_CURRENCY)) {
              passwordErrors.push(
                <small key="indian-currency" className="mt-1 d-block">
                  <FaInfoCircle className="me-1" />
                  {passwordtext?.passwordNotOk}
                </small>
              );
            }
            if (!DIGIT_REGEX.test(value)) {
              passwordErrors.push(
                <small key="digit" className="mt-1 d-block">
                  <FaInfoCircle className="me-1" />
                  {signup?.passValidation.rule2}
                </small>
              );
            }
            if (!UPPERCASE_REGEX.test(value)) {
              passwordErrors.push(
                <small key="uppercase" className="mt-1 d-block">
                  <FaInfoCircle className="me-1" />
                  {signup?.passValidation.rule3}
                </small>
              );
            }
            // Add lowercase check
            if (!LOWERCASE_REGEX.test(value)) {
              passwordErrors.push(
                <small key="lowercase" className="mt-1 d-block">
                  <FaInfoCircle className="me-1" />
                  {signup?.passValidation?.rule5}
                </small>
              );
            }
            if (!ESCAPE_REGEX.test(value)) {
              passwordErrors.push(
                <small key="special" className="mt-1 d-block">
                  <FaInfoCircle className="me-1" />
                  {signup?.passValidation.rule4}
                </small>
              );
            }
            error =
              passwordErrors.length > 0 ? (
                <>
                  {passwordErrors.map((item, index) => (
                    <React.Fragment key={index}>{item}</React.Fragment>
                  ))}
                </>
              ) : (
                <small className="primary-color">
                  <FaCheck className="me-1" />
                  {passwordtext?.okPassword}
                </small>
              );
          }
        }
        break;

      default:
        break;
    }

    // Flatten non-React errors
    if (typeof error === "object" && !React.isValidElement(error)) {
      error = Object.values(error).flat().join(", ");
    }

    if (!silent) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: error
      }));
    }

    return error;
  };

  /** The typed number as E.164, the only form the server is sent. */
  const e164PhoneNumber = () => `+${values.phoneNumber.replace(/\D/g, "")}`;

  /**
   * Who the form says is signing up. The verification request carries all of
   * it, not just the address, because the server makes this signup's CRM
   * records from it while the code is on its way — referralCode included, so
   * that CRM lead reads as referred the same way the account will once
   * /register runs.
   *
   * @returns {{ email: string, firstName: string, lastName: string,
   *   phoneNumber: string, referralCode: string|null }} the identity as
   *   typed, with the number in E.164
   */
  const signupIdentity = () => ({
    email: values.email,
    firstName: values.firstName,
    lastName: values.lastName,
    phoneNumber: e164PhoneNumber(),
    referralCode: referralCode || null
  });

  /**
   * Creating the account starts here. The form is checked and the address is
   * put to the server, which answers whether it owes a code: one that does
   * hands over to the verification step and is created only once the code comes
   * back correct, one that doesn't is created straight away.
   *
   * @param {Event} event the form submit event
   */
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    if (loading.signUpBtn) {
      return;
    }
    if (!validateSignupFields()) {
      return;
    }

    setErrors((prevErrors) => ({ ...prevErrors, submit: "" }));
    setLoading((prev) => ({ ...prev, signUpBtn: true }));
    try {
      const { ok, verificationRequired } =
        await emailOtp.sendCode(signupIdentity());
      if (ok && !verificationRequired) {
        await registerAccount();
      }
    } finally {
      setLoading((prev) => ({ ...prev, signUpBtn: false }));
    }
  };

  /**
   * Confirming the code, which is the point the account is created. Register
   * runs straight after a successful check rather than on another press, so
   * the customer confirms once and lands signed in.
   */
  const handleVerifyCode = async () => {
    if (!(await emailOtp.verifyCode(values.email))) {
      return;
    }
    await registerAccount();
  };

  /** Asks for a replacement code, retiring the one already sent. */
  const handleResendCode = async () => {
    await emailOtp.sendCode(signupIdentity(), { isResend: true });
  };

  /**
   * Back to the form, which is still mounted behind the step and still holds
   * everything that was typed, so only the address needs changing.
   */
  const handleChangeEmail = () => {
    emailOtp.reset();
    setErrors((prevErrors) => ({ ...prevErrors, submit: "" }));
  };

  /**
   * Validates every signup field, showing the errors.
   *
   * @returns {boolean} whether the form is ready to be sent
   */
  const validateSignupFields = () => {
    const firstNameError = validateField("firstName", values.firstName);
    const lastNameError = validateField("lastName", values.lastName);
    const emailError = validateField("email", values.email);
    const phoneNumberError = validateField("phoneNumber", values.phoneNumber);
    const passwordError = validateField("password", values.password);
    const errors = {
      firstName: firstNameError,
      lastName: lastNameError,
      email: emailError,
      phoneNumber: phoneNumberError,
      password: passwordError,
      recaptcha: ""
    };

    setErrors(errors);

    // Check if any errors exist
    return !Object.values(errors).some((error) => error.length > 0);
  };

  // The last step of signup, reached once the emailed code has been confirmed.
  //
  // The reCAPTCHA token is minted here rather than when the form is first
  // submitted: a token taken before the code step would have gone stale by the
  // time the customer has been to their inbox and back.
  const registerAccount = async () => {
    let recaptchaToken = null;

    if (isRecaptchaEnabled(environment)) {
      if (!executeRecaptcha) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          recaptcha: commonErrMsg?.RecaptchaError
        }));
        return;
      }
      recaptchaToken = await executeRecaptcha("signup");
    }

    if (isRecaptchaEnabled(environment)) {
      if (!recaptchaToken) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          recaptcha: login?.errorMSG.recaptcha
        }));
        return;
      }
    }

    try {
      const userdataentry = {
        firstName: cleanName(values.firstName),
        lastName: cleanName(values.lastName),
        email: values.email,
        primaryContactNumber: e164PhoneNumber(),
        password: values.password.trim(),
        recaptchaResponse: recaptchaToken,
        referralCode: referralCode || null,
        promoCode: promoCode || null,
        referralPromoUsageHistoryId: referralPromoUsageHistoryId || null,
        utmHistory: buildUtmHistory(),
        marketingIdentifiers: buildMarketingIdentifiers(),
        ...buildHubspotFormContext()
      };

      setLoading((prev) => ({ ...prev, signUpBtn: true }));
      const response = await publicAxios.post(getRegisterUrl, userdataentry);

      if (response.status === 200 || response.status === 201) {
        showToast(response.data.message, showToastType.success);
        // Cache the role names this account now holds, replacing whatever a previous
        // session on this browser left behind. The cache drives the partner-portal
        // redirect in ProtectedRoute and the account-switching headers, so a stale
        // "ReferralPartner" would bounce this customer to a partner dashboard they
        // cannot access. Registering an existing partner as a customer upgrades that
        // account to hold both roles, and the response carries the updated set — so
        // persisting is right for them too, where discarding would hide their switch
        // headers until the next login. An absent list stores nothing, which leaves
        // ProtectedRoute failing open exactly as it does for an unknown account.
        persistUserRoles(response?.data?.data?.user?.roles ?? []);
        // The registered account owns the ad click that was stored for it, so it is forgotten
        // here rather than credited again to whoever signs up next on this browser.
        clearStoredAdClickIds();
        signupMixpanelEvent(response?.data?.data);
        signupGA4Event(response?.data?.data, promoCode, referralCode);
        navigate(INVOICE, {
          state: { source: NAVIGATION_SOURCE.SIGNUP }
        });
        dispatch(setOnboardingData({}));
        dispatch(setDisconnectConnectedSoftwareData(null));
        dispatch(setGetConnectedSoftwareData({}));
      } else {
        setErrors({ ...errors, submit: response.data.errors });
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          const { message, errors } = error.response.data;
          if (message !== "Validation failed") {
            const errorMessages = Object.values(errors)
              .flatMap((fieldErrors) => {
                if (Array.isArray(fieldErrors)) {
                  return fieldErrors.flat();
                } else if (
                  typeof fieldErrors === "object" &&
                  fieldErrors !== null
                ) {
                  return Object.values(fieldErrors).flat();
                }
                return String(fieldErrors);
              })
              .join("\n");
            setErrors((prevErrors) => ({
              ...prevErrors,
              password: errorMessages ? (
                <small className="mt-1 d-block">
                  <FaExclamationTriangle className="me-1" />
                  {errorMessages}
                </small>
              ) : (
                <small className="primary-color">
                  <FaCheck className="me-1" />
                  {passwordtext?.okPassword}
                </small>
              )
            }));
          } else {
            const { fieldErrors, otherMessages } =
              splitValidationErrors(errors);

            if (Object.keys(fieldErrors).length > 0) {
              setErrors((prevErrors) => ({ ...prevErrors, ...fieldErrors }));
              // The form sits behind the code step whenever the address was
              // verified first, and the input a message belongs to is only
              // visible on the form. Come back to it, so the customer can read
              // what is wrong with the value they typed and change it.
              emailOtp.reset();
            }

            if (otherMessages.length > 0) {
              showToast(otherMessages.join("\n"), showToastType.error);
            }
          }
        } else if (error.response.status === 403) {
          setErrors({ ...errors, submit: error.response.data.message });
        } else if (
          error.response.status === 409 ||
          error.response.status === 500
        ) {
          setErrors({ ...errors, submit: error.response.data.errors });
        } else if (error.response.status === 406) {
          const apiErrors = error.response.data.errors || [];

          if (apiErrors.some((err) => err.includes(RECAPTCHA_ERROR))) {
            setErrors((prev) => ({
              ...prev,
              submit: error.response.data.errors[0]
            }));
            return;
          }

          if (apiErrors.some((err) => err.includes(EXPIRY_ERROR))) {
            setErrors((prev) => ({
              ...prev,
              submit: error.response.data.message
            }));
            return;
          }
        } else {
          setErrors({ ...errors, submit: error.response.data.message });
        }
      } else {
        setErrors((prevErrors) => ({
          ...prevErrors,
          submit: login?.errorMSG.somethingWrong
        }));
      }
    } finally {
      setLoading((prev) => ({ ...prev, signUpBtn: false }));
    }
  };

  useEffect(() => {
    fetchUserLocation();
  }, [fetchUserLocation]);

  const handlePhoneKeyDown = (e) => {
    const input = e.target;

    if (ALLOWED_KEYS.includes(e.key)) {
      return;
    }

    if (!SINGLE_DIGIT_REGEX.test(e.key)) {
      return;
    }

    const digits = input.value.replace(REMOVE_NON_DIGIT_REGEX, "");
    const maxLength = digits.startsWith(AREA_CODES?.nz)
      ? PH_LENGTH_13
      : PH_LENGTH_11;
    const hasSelection = input.selectionStart !== input.selectionEnd;
    if (digits.length >= maxLength && !hasSelection) {
      e.preventDefault();
    }
  };

  const submitButtonContent = loading.signUpBtn ? (
    <>
      <span className="auth-spinner" aria-hidden="true" />
      {signup?.signupBox.submitting}
    </>
  ) : (
    <>
      {signup?.signupBox.submitButton} <FaArrowRight />
    </>
  );

  return (
    <div className="auth-viewport">
      <AuthHeader />
      <main className="auth-page">
        <div className="auth-layout">
          <AuthMechanism who="yum" />
          <div className="auth-right-panel">
            <div className="auth-form-wrap">
              <h1 className="auth-heading">
                {emailOtp.codeSent
                  ? signup?.emailOtp?.heading
                  : signup?.signupBox.heading}
              </h1>
              {!emailOtp.codeSent && (
                <p className="auth-subheading">
                  {signup?.signupBox.subheading}
                </p>
              )}

              {errors.submit && (
                <div className="auth-form-error">
                  <p>{errors.submit}</p>
                </div>
              )}

              {/* A send that failed leaves no step to show the reason on, but
                  the reason still has to be given: the address may already have
                  an account, or have asked for too many codes. */}
              {!emailOtp.codeSent && emailOtp.error && (
                <div className="auth-form-error">
                  <p>{emailOtp.error}</p>
                </div>
              )}

              {emailOtp.codeSent && (
                <EmailVerificationStep
                  email={values.email}
                  code={emailOtp.code}
                  onCodeChange={emailOtp.setCode}
                  error={emailOtp.error}
                  resent={emailOtp.resent}
                  sending={emailOtp.sending}
                  verifying={emailOtp.verifying}
                  creating={loading.signUpBtn}
                  onVerify={handleVerifyCode}
                  onResend={handleResendCode}
                  onBack={handleChangeEmail}
                />
              )}

              {/* The form stays mounted under the step so returning to it
                  brings back everything already typed. */}
              <form
                hidden={emailOtp.codeSent}
                id="signup_form"
                className="auth-page-form"
                onSubmit={handleFormSubmit}
              >
                <div className="auth-name-row">
                  <div className="input-group">
                    <label htmlFor="first_name">
                      {signup?.signupBox.firstname}
                    </label>
                    <input
                      id="first_name"
                      type="text"
                      placeholder="John"
                      name="first_name"
                      onChange={(e) =>
                        handleInputChange("firstName", e.target.value)
                      }
                      onBlur={(e) => validateField("firstName", e.target.value)}
                      value={values.firstName}
                      autoComplete="given-name"
                      aria-invalid={Boolean(errors.firstName)}
                    />
                    {errors.firstName && (
                      <p className="error-message">
                        <FaExclamationCircle
                          className="error-icon"
                          aria-hidden
                        />
                        <small>{errors.firstName}</small>
                      </p>
                    )}
                  </div>
                  <div className="input-group">
                    <label htmlFor="last_name">
                      {signup?.signupBox.lastName}
                    </label>
                    <input
                      id="last_name"
                      type="text"
                      placeholder="Smith"
                      name="last_name"
                      onChange={(e) =>
                        handleInputChange("lastName", e.target.value)
                      }
                      onBlur={(e) => validateField("lastName", e.target.value)}
                      value={values.lastName}
                      autoComplete="family-name"
                      aria-invalid={Boolean(errors.lastName)}
                    />
                    {errors.lastName && (
                      <p className="error-message">
                        <FaExclamationCircle
                          className="error-icon"
                          aria-hidden
                        />
                        <small>{errors.lastName}</small>
                      </p>
                    )}
                  </div>
                </div>

                <div className="input-row">
                  <div className="input-group">
                    <label htmlFor="email">
                      {signup?.signupBox.emailAddress}
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      name="email"
                      onChange={(e) =>
                        handleInputChange("email", e.target.value)
                      }
                      onBlur={(e) => validateField("email", e.target.value)}
                      value={values.email}
                      autoComplete="email"
                      aria-invalid={Boolean(errors.email)}
                    />
                    {errors.email && (
                      <p className="error-message">
                        <FaExclamationCircle
                          className="error-icon"
                          aria-hidden
                        />
                        <small>{errors.email}</small>
                      </p>
                    )}
                  </div>
                </div>

                <div className="input-row">
                  <div className="input-group">
                    <label htmlFor="phone_number">
                      {signup?.signupBox.phoneNumber}
                    </label>
                    <PhoneInput
                      inputProps={{
                        name: "phone_number",
                        id: "phone_number",
                        onKeyDown: handlePhoneKeyDown
                      }}
                      className="ph-with-country-code"
                      country={country}
                      value={values.phoneNumber}
                      countryCodeEditable={false}
                      onlyCountries={["nz", "au"]}
                      areaCodes={AREA_CODES}
                      masks={{ au: "(..) .... ...", nz: "(..) .... ....." }}
                      onChange={(value, event) => {
                        setSelectCountry(event.countryCode);
                        handlePhInputChange(
                          "phoneNumber",
                          value,
                          event.countryCode
                        );
                      }}
                      onBlur={() =>
                        handleBlur("phoneNumber", values.phoneNumber)
                      }
                    />
                    {validatePhnumber && !errors.phoneNumber && (
                      <p className="error-message">
                        <small className="primary-color">
                          <small>
                            <FaCheck /> {signup?.signupBox.phoneOkay}
                          </small>
                        </small>
                      </p>
                    )}
                    {errors.phoneNumber && (
                      <p className="error-message">
                        <FaExclamationCircle
                          className="error-icon"
                          aria-hidden
                        />
                        <small>{errors.phoneNumber}</small>
                      </p>
                    )}
                  </div>
                </div>

                <div className="input-row">
                  <div className="input-group">
                    <label htmlFor="password">
                      {signup?.signupBox.createPass}
                    </label>
                    <div className="passowrd-show">
                      <input
                        id="password"
                        name="password"
                        type={values.showPassword ? "text" : "password"}
                        onChange={(e) => {
                          handleInputChange("password", e.target.value);
                          setErrors((prev) => ({ ...prev, password: "" }));
                        }}
                        onBlur={(e) =>
                          validateField("password", e.target.value)
                        }
                        value={values.password}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                      />
                      <button
                        type="button"
                        className="password-icon"
                        onClick={handleClickShowPassword}
                        aria-label={
                          values.showPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {values.showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="error-message">
                        <small>{errors.password}</small>
                      </p>
                    )}
                  </div>
                </div>

                {errors.recaptcha && (
                  <div className="input-row">
                    <div className="input-group">
                      <p className="error-message" role="alert">
                        <FaExclamationCircle
                          className="error-icon"
                          aria-hidden
                        />
                        <small>{errors.recaptcha}</small>
                      </p>
                    </div>
                  </div>
                )}

                <div className="input-row">
                  <div className="input-group">
                    <button
                      type="submit"
                      className={`auth-btn-primary${
                        values.password ? " is-ready" : ""
                      }`}
                      disabled={loading.signUpBtn}
                    >
                      {submitButtonContent}
                    </button>
                  </div>
                </div>
              </form>

              <p className="auth-toggle-text">
                Already have an account? <Link to={LOGIN}>Log in</Link>
              </p>
            </div>
          </div>
        </div>
      </main>
      <AuthFooter />
    </div>
  );
};

export default Signup;
